import { formatNumber } from "accounting"

import { formatCurrency } from "../../../../Utils/utils"

export const PROGRAMS = { VAMP: "vamp", ECP: "ecp", EFM: "efm" }

export const EFM_REGIONS = { STANDARD: "standard", AU: "au", REGULATED: "regulated" }

export const ECP_TIERS = {
  ECM: { key: "ecm", label: "ECM", count: 100, rate: 1.5 },
  HECM: { key: "hecm", label: "HECM", count: 300, rate: 3.0 }
}

const EFM_LIMITS = {
  [EFM_REGIONS.STANDARD]: { payments: 1000, netFraudVolume: 50000, fraudRate: 0.5, threeDs: 10.0 },
  [EFM_REGIONS.AU]: { payments: 1000, netFraudVolume: 15000, fraudRate: 0.2, threeDs: 10.0 },
  [EFM_REGIONS.REGULATED]: { payments: 1000, netFraudVolume: 50000, fraudRate: 0.5, threeDs: 50.0 }
}

const EFM_REGIME_LABELS = {
  [EFM_REGIONS.AU]: "Australia",
  [EFM_REGIONS.REGULATED]: "regulated"
}

export const formatRate = (value) => `${formatNumber(Number(value ?? 0), 2, ",", ".")}%`

export const formatCount = (value) => formatNumber(Number(value ?? 0), 0, ",", ".")

export const formatMoney = (value) => formatCurrency(Number(value ?? 0), true)

const row = ({ key, label, actual, limit, operator, isReached, isMinimum = false }) => ({
  key,
  label,
  actual,
  limit,
  operator,
  limitText: isMinimum ? `${limit} minimum` : `${operator} ${limit}`,
  isReached,
  isMinimum
})

const isCemea = (thresholds) => Number(thresholds?.volume ?? 0) > 0

const vampLimits = (month, thresholds) => {
  const rateLimit = Number(thresholds?.rate ?? 0)
  const countLimit = Number(thresholds?.count ?? 0)
  const rate = Number(month?.vamp_rate ?? 0)
  const count = Number(month?.vamp_count ?? 0)

  const limits = [
    row({
      key: "vamp_ratio",
      label: "VAMP ratio",
      actual: formatRate(rate),
      limit: formatRate(rateLimit),
      operator: "≥",
      isReached: rate >= rateLimit
    }),
    row({
      key: "incidents",
      label: "Incidents",
      actual: formatCount(count),
      limit: formatCount(countLimit),
      operator: "≥",
      isReached: count >= countLimit
    })
  ]

  if (isCemea(thresholds)) {
    const volumeLimit = Number(thresholds.volume)
    const volume = Number(month?.transaction_volume ?? 0)

    limits.push(
      row({
        key: "payment_volume",
        label: "Payment volume",
        actual: formatMoney(volume),
        limit: formatMoney(volumeLimit),
        operator: "≥",
        isReached: volume >= volumeLimit,
        isMinimum: true
      })
    )
  }

  return limits
}

const ecpLimits = (month) => {
  const tier = month?.tier === ECP_TIERS.HECM.key ? ECP_TIERS.HECM : ECP_TIERS.ECM
  const count = Number(month?.chargeback_count ?? 0)
  const rate = Number(month?.chargeback_rate ?? 0)

  return [
    row({
      key: "chargebacks",
      label: "Chargebacks",
      actual: formatCount(count),
      limit: formatCount(tier.count),
      operator: "≥",
      isReached: count >= tier.count
    }),
    row({
      key: "chargeback_rate",
      label: "Chargeback rate",
      actual: formatRate(rate),
      limit: formatRate(tier.rate),
      operator: "≥",
      isReached: rate >= tier.rate
    })
  ]
}

export const getEfmLimits = (region) => EFM_LIMITS[region] ?? EFM_LIMITS[EFM_REGIONS.STANDARD]

const efmLimits = (month, region) => {
  const limits = getEfmLimits(region)
  const payments = Number(month?.successful_payment_count ?? 0)
  const volume = Number(month?.net_fraud_volume ?? 0)
  const rate = Number(month?.fraud_rate ?? 0)
  const threeDs = month?.three_ds_usage_rate
  const hasThreeDs = threeDs !== null && threeDs !== undefined

  return [
    row({
      key: "payments",
      label: "Mastercard payments",
      actual: formatCount(payments),
      limit: formatCount(limits.payments),
      operator: "≥",
      isReached: payments >= limits.payments,
      isMinimum: true
    }),
    row({
      key: "net_fraud_volume",
      label: "Net fraud volume",
      actual: formatMoney(volume),
      limit: formatMoney(limits.netFraudVolume),
      operator: ">",
      isReached: volume > limits.netFraudVolume
    }),
    row({
      key: "fraud_rate",
      label: "Fraud chargeback rate",
      actual: formatRate(rate),
      limit: formatRate(limits.fraudRate),
      operator: ">",
      isReached: rate > limits.fraudRate
    }),
    row({
      key: "three_ds",
      label: "3DS usage",
      actual: formatRate(threeDs),
      limit: formatRate(limits.threeDs),
      operator: "≤",
      isReached: hasThreeDs && Number(threeDs) <= limits.threeDs
    })
  ]
}

export const evaluateMonthLimits = ({ program, month, thresholds, region }) => {
  if (program === PROGRAMS.VAMP) return vampLimits(month, thresholds)
  if (program === PROGRAMS.ECP) return ecpLimits(month)

  return efmLimits(month, region)
}

export const countReachedLimits = (limits) => limits.filter((limit) => limit.isReached).length

export const getRegimeLabel = ({ program, region, thresholds }) => {
  if (program === PROGRAMS.EFM) return EFM_REGIME_LABELS[region] ?? null
  if (program === PROGRAMS.VAMP) return isCemea(thresholds) ? "CEMEA" : null

  return null
}

export const hasResolvableLimits = ({ program, region, thresholds }) => {
  if (program === PROGRAMS.ECP) return true
  if (program === PROGRAMS.EFM) return Boolean(EFM_LIMITS[region])

  return Number(thresholds?.rate ?? 0) > 0
}
